import { axiosKK } from './networkRequest';
import { BankHolidays } from '../models/APIModels';

/**
 * A calendar with no bank holidays in it.
 *
 * Not a sentinel for "we don't know" — it is what the date pickers should use when the list
 * cannot be had. Every other rule they apply (weekends, the lead-time floor, the working-day
 * arithmetic) is computed locally and stays correct without it; only the bank-holiday
 * closures are missing, and the backend still enforces those at order placement.
 */
export const NO_BANK_HOLIDAYS: BankHolidays = {
  'england-and-wales': { events: [] },
  'northern-ireland': { events: [] },
  scotland: { events: [] },
};

/**
 * The UK bank-holiday calendar, from storage-api.
 *
 * Both date pickers used to call `https://www.gov.uk/bank-holidays.json` from the customer's
 * browser, against the rule that storage-api is the integration boundary, uncached, with
 * `.catch(console.log)` for an error path. storage-api caches the same list for six hours
 * with a static fallback, so this is one request to our own API instead of one to a third
 * party per page load.
 *
 * **It resolves rather than rejects when the request fails**, with an empty calendar. That is
 * the whole point of the change. Before, a failed fetch left `bankHolidays` undefined, the
 * effect that builds the disabled days never ran, and the picker fell back to its initial
 * value of weekends-only — losing the lead-time floor with the bank holidays and offering
 * the customer tomorrow, which checkout would then refuse. A caller cannot forget a failure
 * path that does not exist.
 */
export function fetchBankHolidays(): Promise<BankHolidays> {
  return axiosKK
    .get<BankHolidays>('bank-holidays')
    .then((response) => response?.data ?? NO_BANK_HOLIDAYS)
    .catch((error) => {
      // Logged, not swallowed: the pickers carry on with every rule but this one, and
      // nothing else would say why a bank holiday was bookable.
      console.error('Could not load the bank-holiday calendar', error);
      return NO_BANK_HOLIDAYS;
    });
}
